/**
 * LES PAGES DU SITE — un registre, et trois réglages par page.
 *
 * ⚠️ PORTÉ DU PROTOTYPE (`PAGES_SITE`), y compris l'ordre, les icônes et les
 * sous-titres. Sa leçon est dans son propre commentaire : « Le vrai produit fait la
 * même chose avec ~21 flags de tuiles et une navbar séparée ; ici les deux vivent
 * ensemble. » Deux listes d'interrupteurs qui se répètent finissent par se
 * contredire, et la question de l'organisateur n'est pas « quel drapeau ? » mais
 * « où apparaît cette page ? ».
 *
 * D'où trois clés par page, et une seule ligne pour les régler :
 *
 *   NAV_<PAGE>          dans le menu (bureau) et la barre du bas (mobile)
 *   TILE_<PAGE>         en tuile sur l'accueil
 *   PAGE_<PAGE>_LABEL   son nom — cascadé, donc traduisible et surchargeable
 *
 * ⚠️ ESPACE DE NOMS V2. Le prototype écrit `NAV_PROGRAMME` ; ici c'est
 * `FO_MEETING_V2_NAV_PROGRAMME`, comme tout le reste — la V1 est en production et
 * lit ses propres clés.
 *
 * Les noms par défaut sont français : ils s'affichent tels quels tant que
 * l'organisateur n'a rien écrit.
 */

export type SitePage = {
  /** La clé du prototype : elle nomme la route, les réglages et l'icône. */
  key: string;
  /** Nom affiché tant que l'organisateur n'en a pas écrit un. */
  name: string;
  /** Le nom d'icône de la table partagée. */
  icon: string;
  /** La ligne sous le nom, sur la tuile de l'accueil. Fixe, comme dans le prototype. */
  subtitle: string;
  /**
   * ⚠️ SON ARTICLE INDÉFINI, POUR LES PAGES QU'ON AJOUTE. « Ajouter une section »,
   * « Ajouter un formulaire » : le genre est une propriété du MOT, il ne se devine
   * pas, et un bouton engendré qui dit « une formulaire » se remarque tout de suite.
   * Seules les pages multipliables en ont besoin — elles seules ont un bouton.
   */
  article?: string;
  /**
   * ⚠️ SON ADRESSE, QUAND SA CLÉ NE LA DONNE PAS. Une page servie par une ÉTAPE du
   * parcours répétable vit à `/informations/2` alors que sa clé vaut `informations_2` :
   * la clé nomme ses réglages, le chemin nomme la route, et confondre les deux menait
   * le menu à une adresse qui n'existe pas. Voir `pages/stepPages.ts`.
   */
  path?: string;
  /**
   * ⚠️ LE RÉGLAGE QUI PORTE SON NOM, quand ce n'est pas `PAGE_<CLÉ>_LABEL`. Une page
   * servie par une étape s'appelle comme L'ÉTAPE : deux mots pour une seule chose, et
   * renommer « Informations » dans le parcours laisserait le menu dire l'ancien.
   */
  nameKey?: string;
  /**
   * ⚠️ LE RÉGLAGE QUI PORTE SON SOUS-TITRE, quand il n'est pas celui du registre. Le
   * sous-titre d'une tuile dit ce que la page CONTIENT, fixe tant que le contenu l'est
   * aussi. Une page dont le contenu est CELUI DE L'ORGANISATEUR (la section) ne peut
   * pas porter une description écrite ici : « Accès, horaires, wifi » sous une page
   * qui parle des navettes serait un mensonge intraduisible.
   * Le registre reste le repli quand rien n'est écrit.
   */
  subtitleKey?: string;
  /**
   * ═══ CETTE PAGE PEUT S'OUVRIR AUX GENS QUI NE SONT PAS CONNECTÉS ═══
   *
   * « J'aimerais bien avoir un accueil public avec des sections publiques, on peut y
   * accéder pour les gens non connectés » (2026-08-20). L'organisateur l'ouvre page
   * par page, et rien n'est ouvert tant qu'il ne l'a pas fait.
   *
   * ⚠️ LA CAPACITÉ N'EST PAS LE RÉGLAGE. Elle dit que la page SAIT se rendre sans
   * session ; le réglage `FO_MEETING_V2_PUBLIC_<CLÉ>` dit que l'organisateur le veut.
   * Deux pages seulement portent la capacité, et ce n'est pas un choix de prudence :
   *
   *   — l'ACCUEIL et les SECTIONS ne lisent que la configuration de l'événement, qui
   *     est déjà servie sans jeton (`/context/:alias`) ;
   *   — le badge, l'album, le carnet, la page formulaire, le programme et les étapes
   *     lisent la PERSONNE (`me.id` part dans l'adresse de leurs requêtes). Ouvertes,
   *     elles se rendraient vides, ce qui se lit comme un site en panne. Offrir
   *     l'interrupteur aurait promis quelque chose que le serveur ne sait pas faire.
   */
  publishable?: true;
  /**
   * ═══ CETTE PAGE PEUT EXISTER PLUSIEURS FOIS ═══
   *
   * « On peut en ajouter autant que l'on veut sur l'accueil » : chaque exemplaire est
   * une page à part entière : son nom, son contenu, sa tuile. Même grammaire que
   * l'étape répétable du parcours (`funnel/order.ts`) : la PREMIÈRE garde la clé et
   * l'adresse nues (`infos`, `/infos`), les suivantes se suffixent (`infos_2`,
   * `/infos/2`), ce qui est déjà réglé reste lu. La liste des exemplaires vit dans
   * `pages/repeatable.ts`.
   */
  repeatable?: true;
};

export const SITE_PAGES: ReadonlyArray<SitePage> = [
  {
    key: "programme",
    name: "Programme",
    icon: "programme",
    subtitle: "Conférences et ateliers",
  },
  {
    key: "badge",
    name: "Badge",
    icon: "badge",
    subtitle: "Votre badge d'accès",
  },
  {
    key: "infos",
    /**
     * ⚠️ « SECTION », LE MOT DES ORGANISATEURS. Elle s'appelait « Informations » ;
     * ce que la V1 appelle une SECTION (sa table `Sections`, son
     * `screens/sections/index.jsx`) est exactement cette page-là — un contenu libre,
     * autant qu'on en veut. Deux mots pour une seule chose, et c'est celui que les
     * organisateurs emploient qui gagne.
     *
     * ⚠️ LA CLÉ, ELLE, RESTE `infos`, et l'adresse `/infos` avec elle : ce sont elles
     * qui portent ce que les événements ont DÉJÀ écrit (`FO_MEETING_V2_INFOS_HTML`,
     * `NAV_INFOS`, `TILE_INFOS`, `PAGE_INFOS_LABEL`). Renommer la clé pour suivre le
     * mot aurait vidé toutes les pages en place. Le mot se lit, la clé se range.
     */
    name: "Section",
    article: "une",
    /* Du HTML de l'organisateur, rien d'autre : elle se rend telle quelle pour qui
       n'est pas connecté. C'est la page que la demande visait. */
    publishable: true,
    icon: "infos",
    /**
     * ⚠️ PAS DE DESCRIPTION ÉCRITE ICI, et c'est le mot « section » qui l'a tranché.
     * Elle disait « Accès, horaires, wifi », ce qui décrivait UNE page d'informations
     * — sous une section qui parle des navettes, c'est un mensonge. L'argument est
     * exactement celui qui privait déjà les sections AJOUTÉES de sous-titre : il vaut
     * pour la première aussi. L'organisateur écrit le sien, la tuile n'affiche rien
     * tant qu'il ne l'a pas fait (`nav.tsx` ne pose pas de `<small>` vide).
     */
    subtitle: "",
    repeatable: true,
  },
  {
    key: "contacts",
    name: "Contacts",
    icon: "partenaires",
    subtitle: "Votre carnet de rencontres",
  },
  {
    key: "live",
    name: "En direct",
    icon: "live",
    subtitle: "La diffusion en direct",
  },
  { key: "scan", name: "Scanner", icon: "scan", subtitle: "Scanner un badge" },
  {
    key: "album",
    name: "Album",
    icon: "album",
    subtitle: "Vos photos de l'événement",
  },
  {
    key: "replay",
    name: "Replay",
    icon: "replay",
    subtitle: "Les sessions filmées",
  },
  {
    /**
     * ═══ UNE PAGE QUI SERT UN FORMULAIRE, ET ON EN MET AUTANT QU'ON VEUT ═══
     *
     * « Comme les sections, on peut ajouter des pages qui comportent un formulaire
     * que l'on configure » (2026-08-20). Même grammaire que la section : la première
     * garde clé et adresse nues, les suivantes se suffixent, chacune choisit SON
     * formulaire du back office et porte son nom, son sous-titre, son introduction.
     *
     * ⚠️ ELLE REMPLACE LA PAGE « SATISFACTION », qui était exactement cela en un seul
     * exemplaire et sous un seul nom. Décidé avec l'user : un événement qui veut
     * recueillir des avis ajoute une page formulaire et l'appelle « Satisfaction ».
     * Ce que la V1 rangeait sous son formulaire de satisfaction arrive donc dans la
     * PREMIÈRE page formulaire — c'est l'`origin` du catalogue qui le porte.
     */
    key: "form",
    name: "Formulaire",
    article: "un",
    icon: "formulaire",
    /* Pas de description écrite par le produit, même raison que la section : elle
       parlerait d'un contenu qu'elle ne connaît pas. */
    subtitle: "",
    repeatable: true,
  },
];

/**
 * L'ACCUEIL ET L'INSCRIPTION — dans le gestionnaire comme les autres.
 *
 * Le prototype les écrit en dur dans son menu ; ici l'organisateur peut les éteindre,
 * à sa demande. Deux différences avec les pages de contenu, et elles comptent :
 *
 *   ⚠️ ALLUMÉS PAR DÉFAUT. Une clé absente vaut « affiché », alors qu'elle vaut
 *   « masqué » pour les pages de contenu. L'inverse aurait fait disparaître le menu
 *   de tous les événements existants au prochain chargement, sans que personne
 *   n'ait rien demandé.
 *
 *   ⚠️ LEUR ADRESSE NE SORT PAS DU REGISTRE. L'accueil est la racine ; l'inscription
 *   est la PREMIÈRE ÉTAPE SERVIE, qui dépend des réglages du parcours et se calcule
 *   au rendu (`firstStep`). D'où `tile: false` sur l'accueil : une tuile vers la page
 *   où l'on se trouve déjà.
 */
export const FIXED_PAGES: ReadonlyArray<SitePage & { tile: boolean }> = [
  {
    key: "accueil",
    name: "Accueil",
    icon: "accueil",
    subtitle: "",
    /* Elle ne lit que la configuration : ni la personne, ni ses réponses. */
    publishable: true,
    tile: false,
  },
  {
    key: "inscription",
    name: "Inscription",
    /* ⚠️ NOT `badge`: the two tiles sat side by side on the home screen with the same
       drawing, while they name the two distinct moves of the journey — and the badge
       only exists once the registration is done. */
    icon: "inscription",
    subtitle: "Votre inscription à l'événement",
    /**
     * ⚠️ ELLE PORTE LA CAPACITÉ, ET C'EST L'ORGANISATEUR QUI TRANCHE. Elle était
     * exposée EN DUR aux visiteurs non connectés, au motif que c'est la porte : sans
     * elle, un site public ne permet pas de devenir participant. Le motif est bon, la
     * décision n'était pas la mienne — « c'est nous qui choisissons si on veut
     * l'afficher ou pas ». Elle prend donc son interrupteur comme les autres, et
     * comme les autres elle arrive fermée.
     *
     * Elle SAIT se rendre sans session, contrairement au tunnel qu'elle ouvre : les
     * trois chemins qui y mènent passent par l'écran de connexion quand personne
     * n'est là, en emportant la destination (voir `useEntry`).
     */
    publishable: true,
    tile: true,
  },
];

/** Une page fixe est allumée tant que personne ne l'a éteinte. */
export const isFixed = (key: string) => FIXED_PAGES.some((p) => p.key === key);

/** Toutes les lignes du gestionnaire, dans l'ordre du menu. */
export const MANAGED_PAGES: ReadonlyArray<SitePage> = [
  ...FIXED_PAGES,
  ...SITE_PAGES,
];

const upper = (key: string) => key.toUpperCase();

/**
 * Les trois clés d'une page. Elles ne lisent que sa CLÉ, d'où le paramètre réduit :
 * le catalogue de la reprise les nomme sans avoir de page complète sous la main, et
 * exiger une icône et un sous-titre pour fabriquer un nom de réglage l'aurait obligé
 * à en inventer.
 */
type Nameable = Pick<SitePage, "key">;

/** Dans le menu ? */
export const navKey = (page: Nameable) =>
  `FO_MEETING_V2_NAV_${upper(page.key)}`;
/** En tuile sur l'accueil ? */
export const tileKey = (page: Nameable) =>
  `FO_MEETING_V2_TILE_${upper(page.key)}`;
/** Son nom, cascadé comme n'importe quel texte. */
export const labelKey = (page: Nameable) =>
  `FO_MEETING_V2_PAGE_${upper(page.key)}_LABEL`;

/**
 * OUVERTE AUX GENS QUI NE SONT PAS CONNECTÉS ?
 *
 * ⚠️ ABSENTE VAUT FERMÉE, sans exception et y compris pour l'accueil, dont les deux
 * autres interrupteurs valent pourtant « allumé » quand ils manquent. Ouvrir par
 * défaut aurait rendu public, au prochain chargement, l'accueil de tous les
 * événements existants — le genre de changement qu'on ne remarque qu'une fois
 * dehors.
 *
 * Engendrée comme `navKey` et `tileKey`, donc suffixée pour un exemplaire ajouté :
 * une section peut être publique quand sa voisine ne l'est pas.
 */
export const publicKey = (page: Nameable) =>
  `FO_MEETING_V2_PUBLIC_${upper(page.key)}`;

/**
 * ═══ FERMÉE : PLUS PERSONNE NE L'OUVRE, MÊME PAR SON ADRESSE ═══
 *
 * « Dans le menu » et « en tuile » disent où le LIEN apparaît, pas si la page existe :
 * décochée des deux, `/programme` s'ouvrait toujours à qui tapait l'adresse, et le
 * panneau ne permettait pas de la fermer (bug #334).
 *
 * ⚠️ UN INTERRUPTEUR À PART, PAS LA CONSÉQUENCE DES DEUX AUTRES. Le badge s'atteint par
 * le bouton de la confirmation, le scanner par celui des contacts, une page par un lien
 * envoyé à quelques-uns : autant de pages qu'on sort du menu sans vouloir les fermer.
 * Les déduire des deux coches les aurait fermées sur tous les événements en place.
 *
 * ⚠️ ABSENTE VAUT OUVERTE, strictement `true` ferme : c'est ce que tout événement avait
 * jusqu'ici. Les pages fixes n'en ont pas : l'accueil est la racine, l'inscription est
 * le tunnel, qui a ses propres réglages d'ouverture.
 */
export const closedKey = (page: Nameable) =>
  `FO_MEETING_V2_CLOSED_${upper(page.key)}`;

/**
 * SON SOUS-TITRE, ENGENDRÉ COMME SON NOM.
 *
 * ⚠️ IL N'EN AVAIT AUCUN, ET C'EST CE QUI LE LAISSAIT EN FRANÇAIS. Le sous-titre d'une
 * tuile venait du registre, en dur, sans clé : donc introuvable dans le dictionnaire, donc
 * intraduisible. « Votre inscription à l'événement » s'affichait sous une tuile
 * « Registration » sur un site par ailleurs anglais.
 *
 * ⚠️ AUCUNE LIGNE DE PANNEAU NE LUI CORRESPOND, et ce n'est pas un oubli : ces
 * sous-titres disent ce que la page CONTIENT, ils ne sont pas à la main de
 * l'organisateur. La clé sert au dictionnaire du produit, pas au catalogue de réglages.
 * `useText` lit les deux dans cet ordre, donc le jour où une ligne est ajoutée, elle
 * gagne sans rien changer ici.
 */
export const pageSubtitleKey = (page: Nameable) =>
  `FO_MEETING_V2_PAGE_${upper(page.key)}_SUBTITLE`;

/**
 * A SITE PAGE'S ADDRESS — its key, at the top level: `/badge`.
 *
 * ⚠️ IT THEREFORE SHARES THE EVENT-ALIAS NAMESPACE, and that is a deliberate trade-off
 * (asked for on 2026-08-03; the previous address was `/p/badge`). The first segment
 * names the event (`/:context`); a page declared at the same level wins, because a
 * static segment ranks above a dynamic one — as `/login` already does. Consequence: an
 * event whose alias were "badge", "live" or "album" would no longer be reachable at the
 * root. Checked in the database when the change was made: none of the 292 existing
 * aliases is in that case. The registry's test also forbids a page from taking the
 * address of an already declared screen.
 *
 * ⚠️ AND THAT IS WHY THE PAGES ARE STATIC ROUTES, one per key, rather than a single
 * `/:page`: that dynamic segment would be `/:context`'s RIVAL, at equal score, and the
 * declaration order would decide whether `/mon-salon` names an event or a page. Every
 * event address depended on it.
 */
export const pagePath = (page: SitePage) => page.path ?? `/${page.key}`;

/** La page que sert cette adresse, s'il y en a une. */
export function pageOf(key: string | undefined): SitePage | undefined {
  return SITE_PAGES.find((p) => p.key === key);
}

/**
 * LES pages que l'organisateur peut multiplier. Le routeur leur donne une route
 * d'instance, `screenOf` un préfixe, le gestionnaire un bouton d'ajout chacune.
 *
 * ⚠️ C'ÉTAIT UN SINGULIER (`REPEATABLE_PAGE`, un `find`), et la page formulaire du
 * 2026-08-20 est la seconde : tout ce qui lisait « la » page multipliable ne voyait
 * que la première déclarée, en silence — un `find` ne se plaint jamais. Les listes
 * d'exemplaires, elles, restent une par famille (`FO_MEETING_V2_<CLÉ>_PAGES`) :
 * ranger deux familles dans une clé aurait rendu « 2 » ambigu.
 */
export const REPEATABLE_PAGES: ReadonlyArray<SitePage> = SITE_PAGES.filter(
  (p) => p.repeatable,
);
